import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'store/create-store';
import type {
  ContributionSummaryState,
  ContributionSummaryView,
} from 'store/contributionSummary/types';

export const getContributionSummaryState = (state: RootState): ContributionSummaryState =>
  state.contributionSummary;

// Keyed by recipient_id with cents converted to dollars. Card components read one entry by id.
export const getContributionSummariesByRecipientId = createSelector(
  [getContributionSummaryState],
  (state): Record<string, ContributionSummaryView> => {
    const result: Record<string, ContributionSummaryView> = {};
    for (const [id, summary] of Object.entries(state.data)) {
      result[id] = {
        recipient_id: summary.recipient_id,
        recipient_name: summary.recipient_name,
        election_category: summary.election_category,
        display_info: summary.display_info,
        cycle_total: summary.cycle_total_cents / 100,
        photo_id: summary.photo_id,
        most_recent_donation_date: summary.most_recent_donation_date,
      };
    }
    return result;
  }
);

// Ordered list for the profile "This Cycle" view. Replays the server order held
// in orderedRecipientIds (amount desc, then name asc) through the cents->dollars
// view map — no client-side sort or filter, since the all-recipients fetch already
// returns only positive giving, in order.
export const getContributionSummaryList = createSelector(
  [getContributionSummariesByRecipientId, getContributionSummaryState],
  (byRecipientId, state): ContributionSummaryView[] =>
    state.orderedRecipientIds
      .map(id => byRecipientId[id])
      .filter((view): view is ContributionSummaryView => Boolean(view))
);

const donationTime = (date: string | null) => {
  const parsed = date ? Date.parse(date) : NaN;
  return Number.isNaN(parsed) ? null : parsed;
};

// The same recipients as the "This Cycle" list, re-sorted for the top card's
// photo circles. Built on that list rather than on `data`, which the slate fetch
// also writes to and which can therefore hold recipients with no cycle giving.
export const getCandidatesByRecency = createSelector(
  [getContributionSummaryList],
  (list): ContributionSummaryView[] =>
    [...list].sort((a, b) => {
      const aTime = donationTime(a.most_recent_donation_date);
      const bTime = donationTime(b.most_recent_donation_date);
      if (aTime === bTime) return a.recipient_name.localeCompare(b.recipient_name);
      if (aTime === null) return 1;
      if (bTime === null) return -1;
      return bTime - aTime;
    })
);
